import Badge from 'components/base/Badge';
import EcomTotalOrdersChart from 'components/charts/e-charts/EcomTotalOrdersChart';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingBag } from '@fortawesome/free-solid-svg-icons';

interface OrderStats {
  lastSevenDaysTotal: number;
  percentageChange: number;
  completedPercentage: number;
  pendingPercentage: number;
  dailyCounts: Array<{
    date: string;
    count: number;
    amount: number;
  }>;
}

interface EcomTotalOrdersCardProps {
  orderStats?: OrderStats;
  totalOrders?: number;
  loading?: boolean;
}

const EcomTotalOrdersCard = ({ orderStats, totalOrders = 0, loading = false }: EcomTotalOrdersCardProps) => {
  const { t } = useTranslation();
  // Use provided orderStats or default values if not available
  const stats = orderStats || {
    lastSevenDaysTotal: 0,
    percentageChange: 0,
    completedPercentage: 0,
    pendingPercentage: 0,
    dailyCounts: []
  };
  
  // Format percentage change for display
  const percentageChange = stats.percentageChange || 0;
  const isPositiveChange = percentageChange >= 0;
  const formattedPercentage = `${isPositiveChange ? '+' : ''}${percentageChange.toFixed(1)}%`;
  
  return (
    <Card 
        className="h-100 shadow-sm border-0 overflow-visible"
      style={{
        background: 'var(--phoenix-card-bg, linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%))',
        borderRadius: '0.75rem'
      }}
    >
      <Card.Header className="p-3 d-flex flex-column justify-content-center">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0 fw-bold text-body">
            <FontAwesomeIcon icon={faShoppingBag} className="me-2" />
            {t('totalOrders.title')}
          </h5>
        </div>
      </Card.Header>
      <Card.Body className="pt-0 pb-3 px-3 d-flex flex-column justify-content-center">
        {loading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
            <Spinner animation="border" role="status" variant="primary">
              <span className="visually-hidden">{t('loading')}</span>
            </Spinner>
          </div>
        ) : (
          <>
            <div className="d-flex justify-content-between mb-2">
              <div>
                <h6 className="text-body-tertiary">{t('totalOrders.today')}</h6>
              </div>
              <div>
                <h4 className="mb-0">{totalOrders || stats.lastSevenDaysTotal}
                  <Badge 
                    bg={isPositiveChange ? "success" : "danger"} 
                    variant="phoenix" 
                    pill 
                    className="ms-2"
                    style={{ fontSize: '0.65rem', verticalAlign: 'middle' }}
                  >
                    {formattedPercentage}
                  </Badge>
                </h4>
              </div>
            </div>
            <div className="d-flex justify-content-center px-4 py-6">
              <EcomTotalOrdersChart data={stats.dailyCounts} />
            </div>
            <div className="mt-2">
              <div className="d-flex align-items-center mb-2">
                <div className="bullet-item bg-primary me-2"></div>
                <h6 className="text-body fw-semibold flex-1 mb-0">{t('approved')}</h6>
                <h6 className="text-body fw-semibold mb-0">{stats.completedPercentage}%</h6>
              </div>
              <div className="d-flex align-items-center">
                <div className="bullet-item bg-primary-subtle me-2"></div>
                <h6 className="text-body fw-semibold flex-1 mb-0">
                  {t('orderStats.pendingPayment')}
                </h6>
                <h6 className="text-body fw-semibold mb-0">{stats.pendingPercentage}%</h6>
              </div>
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default EcomTotalOrdersCard;
